import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

interface AdminRowMenuProps {
  children: ReactNode;
  /** The list's `actionMenuPopover` recipe. */
  style?: CSSProperties;
  role?: 'menu';
}

const GAP = 8;

/**
 * Row action menu that is never clipped by the table's scroll box or the
 * panel's rounded edge. It stays in the DOM where it is rendered (inside the
 * row's menu wrapper, so outside-click checks and tab order are unchanged) but
 * is positioned against the viewport from its wrapper's rect, opening upward
 * when there is not enough room below.
 */
export function AdminRowMenu({ children, style, role }: AdminRowMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    const anchor = menu?.parentElement;
    if (!menu || !anchor) return undefined;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const viewportHeight = document.documentElement.clientHeight;
      const viewportWidth = document.documentElement.clientWidth;
      const menuHeight = menu.offsetHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + GAP && rect.top > spaceBelow;
      setPosition({
        top: openUp ? Math.max(GAP, rect.top - GAP - menuHeight) : rect.bottom + GAP,
        right: Math.max(GAP, viewportWidth - rect.right),
      });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, []);

  return (
    <div
      ref={menuRef}
      role={role}
      style={{
        ...style,
        position: 'fixed',
        // Above the list's sticky header (10), below the shell top bar (300).
        zIndex: 20,
        ...(position ?? { top: 0, right: 0, visibility: 'hidden' }),
      }}
    >
      {children}
    </div>
  );
}
